import { useEffect, useState } from "react";
import {
  X,
  ExternalLink,
  Youtube,
  ShieldCheck,
  CheckCircle2,
  Copy,
  Check,
  Play,
  HelpCircle,
} from "lucide-react";
import { VIDEO_GUIDES, YOUTUBE_CHANNEL_URL } from "../../config/videoGuides";

export default function VideoGuideModal({ isOpen, onClose, guideKey = "platform", customGuide = null }) {
  const [copiedIp, setCopiedIp] = useState(false);

  const guide = customGuide || VIDEO_GUIDES[guideKey] || VIDEO_GUIDES.platform;
  const youtubeWatchUrl = guide.videoId
    ? `https://www.youtube.com/watch?v=${guide.videoId}`
    : guide.directUrl || YOUTUBE_CHANNEL_URL;

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleCopyIp = () => {
    if (!guide.whitelistIp) return;
    navigator.clipboard.writeText(guide.whitelistIp);
    setCopiedIp(true);
    setTimeout(() => setCopiedIp(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-950/60">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-red-600/10 border border-red-500/20 text-red-600 flex items-center justify-center flex-shrink-0">
              <Youtube size={18} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate">
                  {guide.title}
                </h3>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                {guide.subtitle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            {/* Direct YouTube link button */}
            <a
              href={youtubeWatchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/50 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
              title="Open video directly on YouTube"
            >
              <Youtube size={14} />
              <span className="hidden sm:inline">Watch on YouTube</span>
              <ExternalLink size={11} />
            </a>

            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Close video modal"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Video Player Container */}
        <div className="overflow-y-auto p-4 sm:p-5 space-y-4">
          <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black border border-gray-200 dark:border-gray-800 shadow-inner">
            {guide.videoId ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${guide.videoId}?autoplay=1&rel=0&modestbranding=1`}
                title={guide.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full border-0"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center p-6 text-center bg-gradient-to-br from-gray-900 via-gray-950 to-black text-gray-300">
                <div className="w-16 h-16 rounded-full bg-red-600/20 border border-red-500/30 text-red-500 flex items-center justify-center mb-4 shadow-lg shadow-red-600/10">
                  <Play size={28} className="translate-x-0.5 fill-current" />
                </div>
                <h4 className="text-base font-bold text-white mb-1">
                  Tutorial Video Preview
                </h4>
                <a
                  href={youtubeWatchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold bg-red-600 hover:bg-red-500 text-white shadow-md shadow-red-600/25 transition-all"
                >
                  <Youtube size={15} /> Open Official YouTube Channel <ExternalLink size={12} />
                </a>
              </div>
            )}
          </div>

          {/* Quick Whitelist IP helper box (if applicable) */}
          {guide.whitelistIp && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 rounded-xl bg-blue-50/60 dark:bg-blue-950/20 border border-blue-200/80 dark:border-blue-900/40 text-xs">
              <div className="flex items-center gap-2.5">
                <ShieldCheck size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <div>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    Delta API Server Whitelist IP:
                  </span>{" "}
                  <code className="px-2 py-0.5 font-mono font-bold text-blue-600 dark:text-blue-400 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-900/60 rounded">
                    {guide.whitelistIp}
                  </code>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCopyIp}
                className="self-start sm:self-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-600 hover:bg-blue-500 text-white transition-colors flex-shrink-0"
              >
                {copiedIp ? <Check size={12} /> : <Copy size={12} />}
                {copiedIp ? "Copied!" : "Copy Whitelist IP"}
              </button>
            </div>
          )}

          {/* Step-by-step checklist */}
          {guide.steps && guide.steps.length > 0 && (
            <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-950/40 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-2 text-xs font-bold text-gray-900 dark:text-gray-100 mb-2.5">
                <CheckCircle2 size={14} className="text-emerald-500" />
                Quick Steps Summary:
              </div>
              <ol className="space-y-1.5 text-xs text-gray-600 dark:text-gray-300 list-decimal list-inside leading-relaxed">
                {guide.steps.map((step, idx) => (
                  <li key={idx} className="pl-1">
                    <span className="text-gray-800 dark:text-gray-200">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Footer note & options */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-1.5">
              <HelpCircle size={13} className="text-gray-400" />
              <span>Need 1-on-1 assistance? Contact support at algomastersinfo@gmail.com</span>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={youtubeWatchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-600 dark:text-red-400 hover:underline font-semibold inline-flex items-center gap-1"
              >
                Open in YouTube <ExternalLink size={10} />
              </a>
              <span>•</span>
              <button
                type="button"
                onClick={onClose}
                className="hover:text-gray-900 dark:hover:text-gray-100 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
